import { useMemo, useState, type FormEvent } from 'react';

import { setContractorCapability } from './capability';
import { navigate } from './path-router';
import { ContractorCard, ContractorIntro, ContractorShell } from './contractor-shell';

/**
 * Rubitime thank-you redirect is a fixed URL (vendor docs) — NOT_PROVEN for
 * tamper-resistant case-specific handoff. This page proves email possession
 * via OTP, then opens the one-case prefill capability (explicit case pick when multi).
 */
export function ContractorContinueApp(props: { readonly apiBaseUrl: string }) {
  const apiBase = props.apiBaseUrl.replace(/\/$/, '');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState<'email' | 'code' | 'select'>('email');
  const [selectionToken, setSelectionToken] = useState<string | null>(null);
  const [cases, setCases] = useState<readonly { case_number: string }[]>([]);
  const [message, setMessage] = useState(
    'После записи в Rubitime проверьте почту CheckTS. Если письма ещё нет — подтвердите email кодом.',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const endpoints = useMemo(
    () => ({
      request: `${apiBase}/public/contractor-access/request-otp`,
      verify: `${apiBase}/public/contractor-access/verify-otp`,
      select: `${apiBase}/public/contractor-access/select-case`,
    }),
    [apiBase],
  );

  const openPrefill = (capability: string) => {
    setContractorCapability(capability);
    navigate('/prefill', 'replace');
  };

  const onRequest = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(endpoints.request, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      setMessage(
        body.message ??
          'Если для этого адреса есть активная запись, мы отправили код подтверждения.',
      );
      setPhase('code');
    } catch {
      setError('Не удалось отправить запрос. Попробуйте позже или откройте ссылку из письма CheckTS.');
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(endpoints.verify, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const body = (await response.json().catch(() => ({}))) as {
        capability?: string;
        case_number?: string;
        selection_required?: boolean;
        selection_token?: string;
        cases?: readonly { case_number: string }[];
        title?: string;
        detail?: string;
      };
      if (response.ok && body.selection_required === true && body.selection_token) {
        setSelectionToken(body.selection_token);
        setCases(body.cases ?? []);
        setPhase('select');
        setMessage('Выберите запись, для которой нужно открыть анкету.');
        return;
      }
      if (!response.ok || body.capability === undefined || body.capability.trim() === '') {
        setError(body.detail ?? body.title ?? 'Неверный email или код.');
        return;
      }
      openPrefill(body.capability);
    } catch {
      setError('Не удалось проверить код. Попробуйте ещё раз.');
    } finally {
      setBusy(false);
    }
  };

  const onSelect = async (caseNumber: string) => {
    if (selectionToken === null) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(endpoints.select, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email,
          selection_token: selectionToken,
          case_number: caseNumber,
        }),
      });
      const body = (await response.json().catch(() => ({}))) as {
        capability?: string;
        title?: string;
        detail?: string;
      };
      if (!response.ok || body.capability === undefined || body.capability.trim() === '') {
        setError(body.detail ?? body.title ?? 'Не удалось открыть выбранную запись.');
        return;
      }
      openPrefill(body.capability);
    } catch {
      setError('Не удалось открыть выбранную запись.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ContractorShell>
      <ContractorIntro
        eyebrow="CheckTS"
        title="Продолжение после записи"
        lead="Запись в Rubitime уже принята CheckTS. Откройте письмо CheckTS со ссылкой на анкету или подтвердите email ниже."
      />
      <ContractorCard title="Доступ к анкете" titleId="contractor-continue-access">
        <p className="contractor-muted">{message}</p>
        <p className="contractor-muted">
          Адрес записи или телефон в адресе страницы сами по себе не открывают анкету.
        </p>
        {phase === 'email' ? (
          <form onSubmit={(event) => void onRequest(event)} className="contractor-form">
            <label>
              Email из записи
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </label>
            <button type="submit" disabled={busy}>
              Получить код
            </button>
          </form>
        ) : null}
        {phase === 'code' ? (
          <form onSubmit={(event) => void onVerify(event)} className="contractor-form">
            <label>
              Код из письма CheckTS
              <input
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
              />
            </label>
            <button type="submit" disabled={busy}>
              Открыть анкету
            </button>
            <button type="button" disabled={busy} onClick={() => setPhase('email')}>
              Изменить email
            </button>
          </form>
        ) : null}
        {phase === 'select' ? (
          <div className="contractor-form">
            <p className="contractor-muted">Для этого email найдено несколько активных записей:</p>
            <ul>
              {cases.map((row) => (
                <li key={row.case_number}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onSelect(row.case_number)}
                  >
                    {row.case_number}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {error !== null ? <p className="contractor-error">{error}</p> : null}
      </ContractorCard>
    </ContractorShell>
  );
}
